// Calendar — the one Home Health page for visits. Replaces the old Schedule
// (AxisCare), Visits and Hours & Auth pages, which were three renderings of
// the same visit feed with three different definitions of "missed".
//
//   Calendar    AxisCare-style month grid (default), per client or agency-wide
//   History     the visit list with period filters, search and missed follow-up
//   Utilisation hours used this month against each client's real PA hours
//
// Everything reads through useVisits, so status means the same thing on every
// tab and on the client profile. Data comes from AxisCare today and from
// CAERA for any service line switched over in the Service Catalog.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subDays, subMonths, isSameMonth } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useModules } from '../../components/RoleGuard';
import { visitClientName, visitCaregiverName, visitActualHours } from '../../lib/axiscare';
import useVisits, { matchesClient } from '../../hooks/useVisits';
import MonthGrid, { Legend, chipStatus, STATUS_STYLE } from '../../components/visits/MonthGrid';
import AuthorizationsPanel, { paUrgency } from '../../components/visits/AuthorizationsPanel';
import VisitList, { visitKey } from '../../components/visits/VisitList';
import { StatTile } from '../../components/StatTile';
import { IN_SERVICE_STATUSES, isReceivingService } from '../../lib/serviceModel';

const iso = (d) => format(d, 'yyyy-MM-dd');
const TABS = [['calendar', 'Calendar'], ['history', 'History'], ['utilisation', 'Utilisation']];
const PERIODS = ['Today', 'This Week', 'This Month', 'Last Month', 'Custom'];
const FILTERS = ['All', 'Completed', 'Scheduled', 'Missed'];
const MONTHS = Array.from({ length: 12 }, (_, i) => format(new Date(2026, i, 1), 'MMM'));

const periodRange = (label) => {
  const t = new Date();
  switch (label) {
    case 'Today':      return { start: iso(t), end: iso(t) };
    case 'This Week':  return { start: iso(subDays(t, 6)), end: iso(t) };
    case 'This Month': return { start: iso(startOfMonth(t)), end: iso(t) };
    case 'Last Month': { const lm = subMonths(t, 1); return { start: iso(startOfMonth(lm)), end: iso(endOfMonth(lm)) }; }
    default:           return { start: iso(startOfMonth(t)), end: iso(t) };
  }
};

const pill = (active) => ({ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: active ? 'var(--accent)' : 'transparent', color: active ? 'var(--white)' : 'var(--text3)', borderColor: active ? 'var(--accent)' : 'var(--border)' });
const seg = (active) => ({ padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: active ? 'var(--accent)' : 'transparent', color: active ? 'var(--white)' : 'var(--text3)' });
const select = { fontSize: 12, padding: '5px 8px', borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontFamily: 'inherit' };
const safeFmt = (v, f) => { const d = new Date(v); return isNaN(d) ? '—' : format(d, f); };

export default function Calendar() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { getAccess } = useModules();
  const [sp, setSp] = useSearchParams();
  const tab = TABS.some(([k]) => k === sp.get('tab')) ? sp.get('tab') : 'calendar';
  const clientId = sp.get('client') || '';
  const month = useMemo(() => {
    const m = sp.get('month');
    return /^\d{4}-\d{2}$/.test(m || '') ? new Date(`${m}-01T12:00:00`) : startOfMonth(new Date());
  }, [sp]);
  const setParam = (patch) => setSp(prev => {
    const next = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => v ? next.set(k, v) : next.delete(k));
    return next;
  }, { replace: true });

  // Active clients for the filter and the utilisation tab.
  const [clients, setClients] = useState([]);
  useEffect(() => {
    supabase.from('clients')
      .select('id, client_id, first_name, last_name, current_status, axiscare_client_id, pa_skilled_hours_week, pa_pss_hours_week, pa_expiry_date, dma_6a_expiry_date, responsible_person_name')
      .in('current_status', IN_SERVICE_STATUSES)
      .order('last_name')
      .then(({ data }) => setClients(data || []));
  }, []);
  const client = useMemo(() => clients.find(c => c.id === clientId) || null, [clients, clientId]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {TABS.map(([k, l]) => <button key={k} style={seg(tab === k)} onClick={() => setParam({ tab: k === 'calendar' ? '' : k })}>{l}</button>)}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {tab !== 'utilisation' && (
            <select style={{ ...select, minWidth: 220 }} value={clientId} onChange={e => setParam({ client: e.target.value })}>
              <option value="">All clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name} · {c.client_id}</option>)}
            </select>
          )}
          <span style={{ fontSize: 9, background: 'var(--accent)', color: 'var(--white)', padding: '2px 7px', borderRadius: 4, fontWeight: 500 }}>AxisCare</span>
        </div>
      </div>

      {tab === 'calendar' && <CalendarTab month={month} setMonth={m => setParam({ month: isSameMonth(m, new Date()) ? '' : format(m, 'yyyy-MM') })} client={client} clients={clients} navigate={navigate} canSchedule={getAccess('scheduling')} />}
      {tab === 'history' && <HistoryTab client={client} profile={profile} />}
      {tab === 'utilisation' && <UtilisationTab clients={clients} month={month} navigate={navigate} />}
    </div>
  );
}

// ── Calendar tab ─────────────────────────────────────────────────────────────
function CalendarTab({ month, setMonth, client, canSchedule, navigate }) {
  const [groupBy, setGroupBy] = useState('client');
  const [selected, setSelected] = useState(null);
  const range = useMemo(() => ({
    start: iso(startOfWeek(startOfMonth(month), { weekStartsOn: 0 })),
    end: iso(endOfWeek(endOfMonth(month), { weekStartsOn: 0 })),
  }), [month]);
  const { visits, loading, error, reload } = useVisits(range, { includeUnassigned: true });
  const shown = useMemo(() => client ? visits.filter(v => matchesClient(v, client)) : visits, [visits, client]);
  const inMonth = useMemo(() => shown.filter(v => isSameMonth(new Date(v.scheduledStartDate || v.startDate), month)), [shown, month]);

  const stats = useMemo(() => ({
    visits: inMonth.filter(v => v._status !== 'Missed').length,
    hours: inMonth.filter(v => v._status !== 'Missed').reduce((s, v) => s + v._hours, 0),
    completed: inMonth.filter(v => v.clockIn && v._status !== 'Missed').length,
    missed: inMonth.filter(v => v._status === 'Missed').length,
    open: inMonth.filter(v => v._status === 'Unassigned').length,
  }), [inMonth]);

  const years = [month.getFullYear() - 1, month.getFullYear(), month.getFullYear() + 1];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month">‹</button>
          <select style={select} value={month.getMonth()} onChange={e => setMonth(new Date(month.getFullYear(), Number(e.target.value), 1))}>
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select style={select} value={month.getFullYear()} onChange={e => setMonth(new Date(Number(e.target.value), month.getMonth(), 1))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month">›</button>
          <button className="btn btn-outline btn-sm" onClick={() => setMonth(startOfMonth(new Date()))}>Today</button>
          {!client && (
            <div style={{ display: 'flex', border: '0.5px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginLeft: 6 }}>
              <button style={seg(groupBy === 'client')} onClick={() => setGroupBy('client')}>By client</button>
              <button style={seg(groupBy === 'caregiver')} onClick={() => setGroupBy('caregiver')}>By caregiver</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="btn btn-outline btn-sm" onClick={reload} title="Reload from AxisCare">↻</button>
          <button className="btn btn-outline btn-sm" onClick={() => window.print()} title="Print">🖨</button>
          {client && <button className="btn btn-outline btn-sm" onClick={() => navigate(`/clients/${client.id}`)}>Client profile</button>}
          {canSchedule && <button className="btn btn-primary btn-sm" onClick={() => navigate('/hh/scheduling')}>Add a visit</button>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, marginBottom: 12 }}>
        <StatTile label="Visits" value={stats.visits} tone="neutral" compact />
        <StatTile label="Hours" value={`${stats.hours.toFixed(1)}h`} tone="neutral" compact />
        <StatTile label="Completed" value={stats.completed} tone="neutral" compact />
        <StatTile label="Missed" value={stats.missed} tone={stats.missed ? 'alert' : 'neutral'} compact />
        <StatTile label="Unassigned" value={stats.open} tone={stats.open ? 'warn' : 'neutral'} compact />
      </div>

      {error && <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warn)', marginBottom: 12 }}>⚠ {error}</div>}

      {loading ? (
        <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <MonthGrid visits={shown} month={month} groupBy={client ? 'service' : groupBy} onSelect={setSelected} selected={selected} />
      )}

      {selected && <VisitDetail v={selected} onClose={() => setSelected(null)} />}

      <div style={{ display: 'grid', gridTemplateColumns: client ? 'minmax(0, 380px) minmax(0, 1fr)' : '1fr', gap: 12, marginTop: 12, alignItems: 'start' }}>
        <div>
          <Legend />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>Day headers show scheduled hours. Week column: S scheduled · B billable (clocked).</div>
        </div>
        {client && <AuthorizationsPanel client={client} visits={shown} month={month} />}
      </div>
    </div>
  );
}

function VisitDetail({ v, onClose }) {
  const st = chipStatus(v);
  const rows = [
    ['Client', visitClientName(v)], ['Caregiver', visitCaregiverName(v)], ['Status', st],
    ['Date', safeFmt(v.scheduledStartDate || v.startDate, 'EEE, MMM d, yyyy')],
    ['Scheduled', `${safeFmt(v.scheduledStartDate || v.startDate, 'h:mm a')} – ${safeFmt(v.scheduledEndDate || v.endDate, 'h:mm a')}`],
    ['Hours', v._hours > 0 ? `${v._hours.toFixed(2)}h` : '—'],
    ['Clock in', v.clockIn?.time ? `${safeFmt(v.clockIn.time, 'h:mm a')} · ${v.clockIn.method || ''}` : 'Not clocked in'],
    ['Clock out', v.clockOut?.time ? `${safeFmt(v.clockOut.time, 'h:mm a')} · ${v.clockOut.method || ''}` : 'Not clocked out'],
    ['Worked', v.clockIn ? `${visitActualHours(v).toFixed(2)}h` : '—'],
    ['Service', `${v.service?.description || v.service?.name || ''} ${v.service?.code ? `(${v.service.code})` : ''}`.trim() || '—'],
    ['Procedure', [v.service?.procedureCode, v.service?.modifier].filter(Boolean).join('-') || '—'],
    ['Charge rate', v.chargeRate != null ? `$${Number(v.chargeRate).toFixed(2)}` : '—'],
    ['Visit id', v.id || v.visitId || '—'],
    ['Source', v._source === 'caera' ? 'CAERA' : 'AxisCare'],
    ['Reason', v.modificationReason ? (typeof v.modificationReason === 'object' ? v.modificationReason.name : v.modificationReason) : '—'],
  ];
  const s = STATUS_STYLE[st];
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-header">
        <div className="card-title">Visit detail <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', borderRadius: 20, background: s.bg, border: `1px solid ${s.border}`, color: s.color }}>{st}</span></div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
        {rows.map(([l, val]) => (
          <div key={l}>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{l}</div>
            <div style={{ fontSize: 13, color: 'var(--text)' }}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── History tab (the old Visits list) ────────────────────────────────────────
function HistoryTab({ client, profile }) {
  const [period, setPeriod] = useState('This Month');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [followedUp, setFollowedUp] = useState({});
  const [toast, setToast] = useState('');
  const [clientByAxisId, setClientByAxisId] = useState({});

  useEffect(() => {
    supabase.from('missed_visit_followups').select('visit_key').then(({ data }) => { if (data) setFollowedUp(Object.fromEntries(data.map(r => [r.visit_key, true]))); });
    supabase.from('clients').select('id, axiscare_client_id').not('axiscare_client_id', 'is', null).then(({ data }) => { if (data) setClientByAxisId(Object.fromEntries(data.map(c => [String(c.axiscare_client_id), c.id]))); });
  }, []);

  const range = useMemo(() => period === 'Custom' ? ((custom.start && custom.end) ? custom : null) : periodRange(period), [period, custom]);
  const { visits, loading, error } = useVisits(range || {});
  const mine = useMemo(() => client ? visits.filter(v => matchesClient(v, client)) : visits, [visits, client]);

  const stats = useMemo(() => {
    const completed = mine.filter(v => v._status === 'Completed').length;
    const scheduled = mine.filter(v => v._status === 'Scheduled').length;
    const missed = mine.filter(v => v._status === 'Missed').length;
    return { total: mine.length, completed, scheduled, missed,
      hours: mine.filter(v => v._status !== 'Missed').reduce((s, v) => s + v._hours, 0),
      rate: (completed + scheduled) > 0 ? Math.round((completed / (completed + scheduled)) * 100) : 0 };
  }, [mine]);

  const list = useMemo(() => {
    let l = mine;
    if (filter !== 'All') l = l.filter(v => v._status === filter);
    if (search) { const q = search.toLowerCase(); l = l.filter(v => visitClientName(v).toLowerCase().includes(q) || visitCaregiverName(v).toLowerCase().includes(q)); }
    return l;
  }, [mine, filter, search]);

  const handleFollowUp = async (v) => {
    const cn = visitClientName(v), cg = visitCaregiverName(v);
    const dt = v.scheduledStartDate || v.startDate;
    const key = visitKey(v);
    setFollowedUp(p => ({ ...p, [key]: true }));
    const { data: task } = await supabase.from('tasks').insert([{
      title: `Follow up — missed visit: ${cn}`,
      notes: `Missed visit on ${dt ? format(new Date(dt), 'MMM d, yyyy') : '?'} · Caregiver: ${cg}`,
      status: 'Open', priority: 'Urgent',
      assigned_to_id: profile?.id, assigned_to_name: profile?.full_name,
      created_by_id: profile?.id, created_by_name: profile?.full_name,
      due_date: format(new Date(), 'yyyy-MM-dd'),
    }]).select('id').single();
    await supabase.from('missed_visit_followups').upsert({
      visit_key: key, client_name: cn, caregiver_name: cg,
      client_uuid: clientByAxisId[String(v.client?.id)] || null,
      visit_date: dt ? format(new Date(dt), 'yyyy-MM-dd') : null,
      task_id: task?.id || null,
      followed_up_by_id: profile?.id, followed_up_by_name: profile?.full_name,
    }, { onConflict: 'visit_key' });
    setToast(`Follow-up task created for ${cn}`);
    setTimeout(() => setToast(''), 3000);
  };

  return (
    <div>
      {toast && <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '9px 14px', marginBottom: 12, fontSize: 12, color: 'var(--success)' }}>✓ {toast}</div>}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {PERIODS.map(p => <button key={p} style={pill(period === p)} onClick={() => setPeriod(p)}>{p}</button>)}
        {period === 'Custom' && (
          <>
            <input type="date" className="form-input" style={{ width: 150 }} value={custom.start} onChange={e => setCustom(p => ({ ...p, start: e.target.value }))} />
            <span style={{ color: 'var(--text3)' }}>to</span>
            <input type="date" className="form-input" style={{ width: 150 }} value={custom.end} onChange={e => setCustom(p => ({ ...p, end: e.target.value }))} />
          </>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 8, marginBottom: 12 }}>
        {[
          { label: 'Total', value: stats.total, tone: 'neutral' },
          { label: 'Completed', value: stats.completed, tone: 'neutral' },
          { label: 'Scheduled', value: stats.scheduled, tone: 'neutral' },
          { label: 'Missed', value: stats.missed, tone: stats.missed > 0 ? 'alert' : 'neutral' },
          { label: 'Hours', value: `${stats.hours.toFixed(1)}h`, tone: 'neutral' },
          { label: 'Compliance', value: `${stats.rate}%`, tone: stats.rate >= 90 ? 'neutral' : stats.rate >= 70 ? 'warn' : 'alert' },
        ].map(s => <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} compact />)}
      </div>
      {error && <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warn)', marginBottom: 12 }}>⚠ {error}</div>}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Visit history</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input placeholder="Search client or caregiver…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', width: 200, fontFamily: 'inherit' }} />
            {FILTERS.map(f => <button key={f} style={{ ...pill(filter === f), padding: '3px 10px', fontSize: 11 }} onClick={() => setFilter(f)}>{f}{f === 'Missed' && stats.missed > 0 ? ` (${stats.missed})` : ''}</button>)}
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{list.length} visits</span>
          </div>
        </div>
        {loading ? <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
          : list.length === 0 ? (
            <div className="empty-state">
              {filter === 'Missed' ? <><div style={{ fontSize: 32, marginBottom: 8 }}>✓</div><p style={{ fontWeight: 500, color: 'var(--success)' }}>No missed visits this period.</p></> : <p>No visits found</p>}
            </div>
          ) : <VisitList visits={list} mode={filter === 'Missed' ? 'missed' : 'standard'} onFollowUp={handleFollowUp} followedUp={followedUp} />}
      </div>
    </div>
  );
}

// ── Utilisation tab (the old Hours & Auth, now against real PA hours) ────────
function UtilisationTab({ clients, month, navigate }) {
  const [filter, setFilter] = useState('All');
  const [sortKey, setSortKey] = useState('last_name');
  const [sortAsc, setSortAsc] = useState(true);
  const range = useMemo(() => ({ start: iso(startOfMonth(month)), end: iso(endOfMonth(month)) }), [month]);
  const { visits, loading, error } = useVisits(range);
  const daysInMonth = endOfMonth(month).getDate();
  const daysPassed = isSameMonth(month, new Date()) ? new Date().getDate() : (month < new Date() ? daysInMonth : 0);

  const rows = useMemo(() => clients.filter(c => isReceivingService(c)).map(c => {
    const mine = visits.filter(v => matchesClient(v, c) && v._status !== 'Missed');
    const used = mine.reduce((s, v) => s + v._hours, 0);
    const weekly = (Number(c.pa_skilled_hours_week) || 0) + (Number(c.pa_pss_hours_week) || 0);
    const auth = weekly > 0 ? weekly * (daysInMonth / 7) : null;
    const pct = auth ? Math.min((used / auth) * 100, 100) : null;
    return { ...c, used, auth, pct, visits: mine.length, completed: mine.filter(v => v.clockIn).length, urgency: paUrgency(c) };
  }), [clients, visits, daysInMonth]);

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
    const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortAsc ? cmp : -cmp;
  }), [rows, sortKey, sortAsc]);
  const filtered = filter === 'All' ? sorted
    : filter === 'Near limit' ? sorted.filter(c => c.pct != null && c.pct >= 70)
    : filter === 'No hours' ? sorted.filter(c => c.used === 0)
    : filter === 'No PA hours' ? sorted.filter(c => c.auth == null)
    : sorted.filter(c => c.urgency && c.urgency.tone !== 'var(--success)');

  const totals = {
    hours: rows.reduce((s, c) => s + c.used, 0),
    near: rows.filter(c => c.pct != null && c.pct >= 70).length,
    expiring: rows.filter(c => c.urgency && c.urgency.tone !== 'var(--success)').length,
    noPa: rows.filter(c => c.auth == null).length,
  };
  const barColor = (pct) => pct == null ? 'var(--border2)' : pct >= 90 ? 'var(--danger)' : pct >= 70 ? 'var(--warn)' : 'var(--success)';

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, marginBottom: 12 }}>
        <StatTile label="Active clients" value={rows.length} tone="neutral" compact />
        <StatTile label={`Hours · ${format(month, 'MMM yyyy')}`} value={`${totals.hours.toFixed(1)}h`} tone="neutral" compact />
        <StatTile label="Near auth limit" value={totals.near} tone={totals.near ? 'warn' : 'neutral'} compact />
        <StatTile label="PA expiring" value={totals.expiring} tone={totals.expiring ? 'alert' : 'neutral'} compact />
        <StatTile label="No PA hours on file" value={totals.noPa} tone={totals.noPa ? 'warn' : 'neutral'} compact />
      </div>
      {error && <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warn)', marginBottom: 12 }}>⚠ {error}</div>}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Hours used vs PA authorization</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <select style={select} value={sortKey} onChange={e => setSortKey(e.target.value)}>
              <option value="last_name">Sort: Name</option><option value="used">Sort: Hours</option><option value="pct">Sort: % used</option><option value="pa_expiry_date">Sort: PA expiry</option>
            </select>
            <button className="btn btn-outline btn-sm" onClick={() => setSortAsc(a => !a)}>{sortAsc ? '↑' : '↓'}</button>
            {['All', 'Near limit', 'No hours', 'Expiring auth', 'No PA hours'].map(f => <button key={f} style={{ ...pill(filter === f), padding: '3px 10px', fontSize: 11 }} onClick={() => setFilter(f)}>{f}</button>)}
          </div>
        </div>
        {loading ? <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
          : filtered.length === 0 ? <div className="empty-state"><p>No clients match</p></div>
          : (
            <div style={{ padding: '8px 16px', maxHeight: 'calc(100vh - 330px)', overflowY: 'auto' }}>
              {filtered.map(c => (
                <div key={c.id} style={{ padding: '10px 0', borderBottom: '0.5px solid var(--border)', cursor: 'pointer' }} onClick={() => navigate(`/hh/calendar?client=${c.id}`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 8 }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--accent2)' }}>{c.last_name}, {c.first_name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8, fontFamily: 'var(--mono)' }}>{c.client_id}</span>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                        {c.completed} of {c.visits} visits completed · PA {c.pa_skilled_hours_week || 0}h skilled + {c.pa_pss_hours_week || 0}h PSS per week
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: barColor(c.pct) }}>{c.used.toFixed(1)}h <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>of {c.auth != null ? `${c.auth.toFixed(0)}h` : 'no PA hours'}</span></div>
                        {c.pct != null && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{Math.round(c.pct)}% used</div>}
                      </div>
                      {c.urgency && <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: c.urgency.bg, color: c.urgency.tone }}>{c.urgency.label}</span>}
                    </div>
                  </div>
                  <div style={{ background: 'var(--border)', borderRadius: 4, height: 8, position: 'relative' }}>
                    <div style={{ background: barColor(c.pct), height: 8, borderRadius: 4, width: `${c.pct ?? 0}%`, transition: 'width 0.5s' }} />
                    <div style={{ position: 'absolute', top: -2, left: `${(daysPassed / daysInMonth) * 100}%`, width: 2, height: 12, background: 'var(--text3)', borderRadius: 1, opacity: 0.5 }} title="Today" />
                  </div>
                  {c.pct != null && c.pct >= 90 && <div style={{ fontSize: 10, color: 'var(--danger)', marginTop: 3 }}>⚠ Near authorization limit</div>}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
